type PlaceholderValues = Record<string, string>;

type ProductPlaceholderSource = {
  productHandle: string;
  applicationRate: number;
  amountInKgOrLiter: number;
};

type InformationItem = {
  title: string;
  description: unknown;
};

const placeholderPattern = /\{\{([^{}]+)\}\}/g;

const formatPlaceholderNumber = (value: number) => {
  if (!Number.isFinite(value)) {
    return "";
  }

  return String(Number(value.toFixed(2)));
};

export const getBundlePlaceholderValues = (
  productVariants: ProductPlaceholderSource[] = [],
): PlaceholderValues =>
  productVariants.reduce<PlaceholderValues>((placeholderValues, product) => {
    if (
      !product.productHandle ||
      !Number.isFinite(product.applicationRate) ||
      product.applicationRate <= 0 ||
      !Number.isFinite(product.amountInKgOrLiter)
    ) {
      return placeholderValues;
    }

    return {
      ...placeholderValues,
      [`${product.productHandle}-amount`]: formatPlaceholderNumber(
        product.amountInKgOrLiter,
      ),
      [`${product.productHandle}-coverage`]: formatPlaceholderNumber(
        (product.amountInKgOrLiter * 1000) / product.applicationRate,
      ),
    };
  }, {});

export const getLawnSizePlaceholderValues = (
  lawnSize?: number | null,
): PlaceholderValues => {
  if (typeof lawnSize !== "number" || !Number.isFinite(lawnSize)) {
    return {};
  }

  return {
    "lawn-size": formatPlaceholderNumber(lawnSize),
  };
};

const replacePlaceholdersInString = (
  value: string,
  placeholderValues: PlaceholderValues,
) =>
  value.replace(
    placeholderPattern,
    (placeholder, key: string) => placeholderValues[key] ?? placeholder,
  );

export const replacePlaceholders = <T>(
  value: T,
  placeholderValues: PlaceholderValues,
): T => {
  if (typeof value === "string") {
    return replacePlaceholdersInString(value, placeholderValues) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) =>
      replacePlaceholders(item, placeholderValues),
    ) as T;
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entryValue]) => [
        key,
        replacePlaceholders(entryValue, placeholderValues),
      ]),
    ) as T;
  }

  return value;
};

export const replaceInformationItemPlaceholders = <
  TInformationItem extends InformationItem,
>(
  informationItems: TInformationItem[],
  placeholderValues: PlaceholderValues,
): TInformationItem[] =>
  informationItems.map((item) => ({
    ...item,
    title: replacePlaceholders(item.title, placeholderValues),
    description: replacePlaceholders(item.description, placeholderValues),
  }));
